import React, { useContext } from 'react';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { Badge, Divider, IconButton, Menu } from '@mui/material';
import { Box } from '@mui/system';
import { Text } from '../elements/text';
import CloseIcon from '@mui/icons-material/Close';
import { CustomButton } from '../elements/button';
import { NOTIFICATION_SUBSCRIPTION } from '../graphql/subscriptions';
import { useSubscription } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { NotificationContext } from '../context/notification-context';
import { GET_ALL_NOTIFICATIONS } from '../graphql/query';
import { MARK_AS_SEEN } from '../graphql/mutations';
import { NotificationOut } from '../types/notification';
import { AppContext } from '../context/app-context';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { extractAllErrorMesasges } from '../utils/graphql';
import { useQueryHook } from '../hooks/use-query.hook';
import { useMutationHook } from '../hooks/use-mutation.hook';

export const Notification = () => {
  const { runner } = useContext(AppContext);
  const runnerID = runner?.value?._id;
  const { user } = useAuthenticator();
  const token = user.getSignInUserSession()?.getAccessToken()?.getJwtToken();
  const { enqueueSnackbar } = useSnackbar();
  const notificationContext = useContext(NotificationContext);

  const { data, fetch: fetchNotifications } = useQueryHook({
    query: GET_ALL_NOTIFICATIONS,
    variables: {
      payload: {
        filter: {
          subscriberID: runnerID,
          seen: false,
        },
        limit: 10,
      },
    },
  });

  useSubscription(NOTIFICATION_SUBSCRIPTION, {
    variables: {
      subscriberID: runnerID,
    },
    onSubscriptionData: (data: any) => {
      enqueueSnackbar(
        data?.subscriptionData?.data?.onCreateNotification?.message,
        {
          autoHideDuration: 5000,
          variant: 'success',
        },
      );

      fetchNotifications({
        payload: {
          filter: {
            subscriberID: runner?.value?._id,
            seen: false,
          },
          limit: 10,
        },
      });
      notificationContext.callSubscribers();
      notificationContext.callSubscribers();
    },
  });

  const throwErrorMessages = (errorMessages: string[]) => {
    errorMessages.map((err) => {
      enqueueSnackbar(err, {
        autoHideDuration: 5000,
        variant: 'error',
      });
    });
  };

  const { mutate: markAsSeen } = useMutationHook({
    mutation: MARK_AS_SEEN,
    onError: (err) => {
      throwErrorMessages(extractAllErrorMesasges(err));
    },
  });

  const notifications = data?.getAllNotifications || [];
  const badgeContent = notifications.length > 9 ? '9+' : notifications.length;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onCloseNotificationHandler = async (notification: NotificationOut) => {
    await markAsSeen({
      variables: {
        ids: [notification._id],
      },
    });

    await fetchNotifications({
      payload: {
        filter: {
          subscriberID: runner?.value?._id,
          seen: false,
        },
        limit: 10,
      },
    });
  };

  const clearAllNotificaions = async () => {
    await markAsSeen({
      variables: {
        ids: notifications.map((n: NotificationOut) => n._id),
      },
    });

    await fetchNotifications({
      payload: {
        filter: {
          subscriberID: runner?.value?._id,
          seen: false,
        },
        limit: 10,
      },
    });
    handleClose();
  };

  const goToOrderPage = async (notification: NotificationOut) => {
    let body = JSON.parse(notification.body || `{}`);
    //TODO: A saftey check, if in case body in the db saved as string re parse.
    if (typeof body == 'string' || body instanceof String) {
      body = JSON.parse(body.toString());
    }
    if (notification.type == 'ORDER') {
      // TODO: Navigate based on body.status
      await markAsSeen({
        variables: {
          ids: [notification._id],
        },
      });
      await fetchNotifications({
        payload: {
          filter: {
            subscriberID: runner?.value?._id,
            seen: false,
          },
          limit: 10,
        },
      });
    }

    setAnchorEl(null);
  };

  return (
    <Box>
      <IconButton sx={{ padding: '5px' }} onClick={handleClick}>
        <Badge badgeContent={badgeContent} color="primary">
          <NotificationsActiveIcon
            color={notifications.length == 0 ? 'disabled' : 'action'}
          />
        </Badge>
      </IconButton>
      {notifications.length > 0 && (
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          onClose={handleClose}
          open={open}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
          sx={{ width: '90%', maxWidth: '90%' }}
        >
          <Box maxHeight={'500px'} width={{ sm: 500, xs: 300 }}>
            {notifications?.map(
              (notification: NotificationOut, index: number) => {
                return (
                  <Box
                    sx={{
                      ':hover': {
                        background: '#ededed',
                      },
                    }}
                    key={notifications._id + '_' + index}
                  >
                    <Box display={'flex'} p={2}>
                      <Box
                        onClick={() => goToOrderPage(notification)}
                        width={'90%'}
                        mt={1}
                        sx={{ cursor: 'pointer', wordBreak: 'break-all' }}
                      >
                        <Text
                          fontWeight={700}
                          fontSize={16}
                          text={notification?.message?.toString()}
                        />
                      </Box>
                      <Box sx={{ cursor: 'pointer' }}>
                        <IconButton
                          onClick={() =>
                            onCloseNotificationHandler(notification)
                          }
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box width="100%">
                      <Divider />
                    </Box>
                  </Box>
                );
              },
            )}
            <Box p={2}>
              <CustomButton title="Clear All" onClick={clearAllNotificaions} />
            </Box>
          </Box>
        </Menu>
      )}
    </Box>
  );
};
